import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Badge, Menu, Button } from 'antd';
import Translate from 'react-translate-component';
import { isNil } from 'ramda';
import cx from 'classnames';

import CustomIcon from 'components/common/CustomIcon';
import { closeAllCollapsible, setCollpasibleActive, collapseModals, closeCollapsible, afterCloseCollapsible } from 'actions/modalActions';
import { COLLAPSIBLE_MODAL_COMPONENTS, COLLAPSIBLE_MODAL_ICONS, COLLAPSIBLE_MODAL_CLASSNAME } from 'constants/collapsibleModals';
import { getCollapsibleTitle } from 'utils/collapsibleModals';
import CatchError from 'components/common/CatchError';

const renderContent = (modal, close) => {
    const Component = COLLAPSIBLE_MODAL_COMPONENTS[modal.type];

    return <Component params={modal.params} close={() => close()} />;
}

const CollapsibleModalError = ({ reset, close, visible }) => visible && (
    <Modal width={300} visible={visible} footer={null} closable={false} className="modal-error">
        <div className="catched-error">
            <Translate content="errors.couldNotOpenWindow" component="h3" className="m-b-15" />
            <Button type="blue" onClick={reset}>
                <Translate content="common.refresh" />
            </Button>
            <Button onClick={close}>
                <Translate content="modal_settings.cancel" />
            </Button>
        </div>
    </Modal>
);

const Modals = ({ modals, active, close }) => {
    return modals.map((modal, index) => (
        <div
            key={`collapsible-modal-${modal.id}`}
            className={cx('collapsible-modal-content', { show: index === active })}
        >
            { renderContent(modal, close) }
        </div>
    ));
}

const ModalsMenu = ({ modals, active, closeAll, close, setActive }) => {
    return (
        <div className='modal-sidebar'>
            <div className='modal-sidebar-header'>
                <Translate content='modal.opened' /> <Badge count={modals.length} />
            </div>
            <Menu
                mode='inline'
                selectedKeys={!isNil(active) ? [active.toString()] : null}
            >
                { modals.map((modal, index) =>
                    <Menu.Item
                        key={index.toString()}
                        onClick={() => setActive(index)}>
                        <CustomIcon type={COLLAPSIBLE_MODAL_ICONS[modal.type]} />
                        { getCollapsibleTitle(modal) }
                        <CustomIcon className='btn-menu' type='close-remove' onClick={(e) => {
                            e.stopPropagation();
                            close(index);
                        }} />
                    </Menu.Item>
                )}
            </Menu>
            <div className='modal-sidebar-footer'>
                <small>
                    <a
                        className='header-inline-btn'
                        onClick={() => closeAll()}
                    >
                        <Translate content='modal.close_all' />
                    </a>
                </small>
            </div>
        </div>
    );
}

export const collapsibleModalPropTypes = {
    close: PropTypes.func.isRequired,
    params: PropTypes.any,
};

const stateToProps = ({ collapsibleModals }) => ({
    active: collapsibleModals.active,
    modals: collapsibleModals.modals,
    visible: !collapsibleModals.collapsed
});

@connect(stateToProps, {
    closeAll: closeAllCollapsible,
    setActive: setCollpasibleActive,
    collapse: collapseModals,
    close: closeCollapsible,
    afterClose: afterCloseCollapsible
})
export default class CollapsibleModals extends Component {
    static propTypes = {
        active: PropTypes.number,
        modals: PropTypes.array,
        visible: PropTypes.bool,
        closeAll: PropTypes.func,
        setActive: PropTypes.func,
        collapse: PropTypes.func,
        close: PropTypes.func,
        afterClose: PropTypes.func
    };

    setActive = index => {
        const { active, setActive } = this.props;

        if (index !== active) {
            setActive(index);
        }
    }

    close = (index = this.props.active) => {
        const { close, closeAll, modals } = this.props;

        modals.length > 1 ? close(index) : closeAll();
    }

    getClassName = current => {
        return current ? COLLAPSIBLE_MODAL_CLASSNAME[current.type] : '';
    }

    renderOnError = (reset) => <CollapsibleModalError reset={reset} close={this.props.closeAll} visible={this.props.visible} />;

    render() {
        const { visible, modals, active, afterClose, closeAll, collapse } = this.props;
        const current = modals[active];
        const showModalsMenu = modals.length > 1;

        return (
            <Modal
                className={cx('collapsible-modal', this.getClassName(current), { 'menu-active': showModalsMenu })}
                visible={visible}
                footer={null}
                width={768}
                onCancel={() => this.close()}
                afterClose={afterClose}
            >
                <div className='modal-collapse-icon' onClick={collapse}>_</div>
                <CatchError renderOnError={this.renderOnError} reset={!this.props.modals.length}>
                    <Modals
                        modals={modals}
                        active={active}
                        close={this.close}
                    />
                    { showModalsMenu && (
                        <ModalsMenu
                            modals={modals}
                            active={active}
                            closeAll={closeAll}
                            collapse={collapse}
                            close={this.close}
                            setActive={this.setActive}
                        />
                    )}
                </CatchError>
            </Modal>
        );
    }
}
